export type LoyaltyAsset = 'growth' | 'memory_seed';
export type LoyaltyEventType =
  | 'growth_accrued'
  | 'growth_reversed'
  | 'seed_accrued'
  | 'seed_reversed'
  | 'manual_adjustment'
  | 'asset_transfer';

export interface LoyaltyBalance {
  readonly growth: number;
  readonly memorySeed: number;
}

export interface LoyaltyEvent {
  readonly eventId: string;
  readonly type: LoyaltyEventType;
  readonly customerId: string;
  readonly asset: LoyaltyAsset;
  readonly quantity: number;
  readonly sourceType: 'sale' | 'refund' | 'manual' | 'customer_merge';
  readonly referenceId: string;
  readonly idempotencyKey: string;
  readonly at: string;
  readonly actorId?: string;
  readonly reason?: string;
  readonly orderId?: string;
  readonly orderRevision?: number;
}

export interface AccrueSaleCommand {
  readonly customerId: string;
  readonly orderId: string;
  readonly orderRevision: number;
  readonly currency: 'HKD';
  readonly eligibleMinor: number;
  readonly idempotencyKey: string;
  readonly at: string;
}

export interface AccrueSaleResult {
  readonly customerId: string;
  readonly orderId: string;
  readonly orderRevision: number;
  readonly eligibleMinor: number;
  readonly growthQuantity: number;
  readonly memorySeedQuantity: number;
  readonly balance: LoyaltyBalance;
}

export interface ReverseRefundCommand {
  readonly customerId: string;
  readonly orderId: string;
  readonly orderRevision: number;
  readonly refundEvidenceId: string;
  readonly refundMinor: number;
  readonly idempotencyKey: string;
  readonly at: string;
}

export interface ReverseRefundResult {
  readonly customerId: string;
  readonly orderId: string;
  readonly refundEvidenceId: string;
  readonly reversedGrowthQuantity: number;
  readonly reversedMemorySeedQuantity: number;
  readonly totalReversedGrowthQuantity: number;
  readonly totalReversedMemorySeedQuantity: number;
  readonly balance: LoyaltyBalance;
}

export interface ManualAdjustmentCommand {
  readonly customerId: string;
  readonly asset: LoyaltyAsset;
  readonly quantity: number;
  readonly actorId: string;
  readonly reason: string;
  readonly idempotencyKey: string;
  readonly at: string;
}

export interface ManualAdjustmentResult {
  readonly event: LoyaltyEvent;
  readonly balance: LoyaltyBalance;
}

export interface TransferCustomerAssetsCommand {
  readonly sourceCustomerId: string;
  readonly targetCustomerId: string;
  readonly actorId: string;
  readonly reason: string;
  readonly idempotencyKey: string;
  readonly at: string;
}

export interface TransferCustomerAssetsResult {
  readonly sourceBalance: LoyaltyBalance;
  readonly targetBalance: LoyaltyBalance;
  readonly events: readonly LoyaltyEvent[];
}

interface AccrualRecord {
  readonly customerId: string;
  readonly orderId: string;
  readonly orderRevision: number;
  readonly eligibleMinor: number;
  readonly accruedUnits: number;
  cumulativeRefundMinor: number;
  reversedUnits: number;
}

interface IdempotentResult {
  readonly signature: string;
  readonly result: unknown;
}

const clone = <T>(value: T): T => structuredClone(value);

const stableValue = (value: unknown): unknown => {
  if (Array.isArray(value)) return value.map(stableValue);
  if (value && typeof value === 'object') return Object.fromEntries(
    Object.entries(value as Record<string, unknown>)
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([key, entry]) => [key, stableValue(entry)]),
  );
  return value;
};

const signatureOf = (kind: string, command: unknown): string =>
  JSON.stringify(stableValue({ kind, command }));

const assertText = (value: string, code: string): string => {
  const accepted = value?.trim();
  if (!accepted) throw new Error(code);
  return accepted;
};

const assertTime = (value: string): void => {
  if (!Number.isFinite(Date.parse(value))) throw new Error('LOYALTY_TIMESTAMP_INVALID');
};

const assertPositiveMinor = (value: number, code: string): void => {
  if (!Number.isSafeInteger(value) || value <= 0) throw new Error(code);
};

const assertOrderRevision = (value: number): void => {
  if (!Number.isSafeInteger(value) || value <= 0) throw new Error('LOYALTY_ORDER_REVISION_INVALID');
};

const orderKey = (orderId: string): string => orderId.trim();

export function createLoyaltyLedger() {
  const ledgerEvents: LoyaltyEvent[] = [];
  const accruals = new Map<string, AccrualRecord>();
  const appliedRefundEvidence = new Set<string>();
  const idempotency = new Map<string, IdempotentResult>();
  let eventSequence = 0;

  const balanceFor = (customerId: string): LoyaltyBalance => {
    const accepted = assertText(customerId, 'LOYALTY_CUSTOMER_ID_REQUIRED');
    let growth = 0;
    let memorySeed = 0;
    for (const event of ledgerEvents) {
      if (event.customerId !== accepted) continue;
      if (event.asset === 'growth') growth += event.quantity;
      else memorySeed += event.quantity;
    }
    if (growth < 0 || memorySeed < 0) throw new Error('LOYALTY_BALANCE_NEGATIVE');
    return { growth, memorySeed };
  };

  const run = <T>(kind: string, idempotencyKey: string, command: unknown, action: () => T): T => {
    const key = assertText(idempotencyKey, 'LOYALTY_IDEMPOTENCY_REQUIRED');
    const signature = signatureOf(kind, command);
    const replay = idempotency.get(key);
    if (replay) {
      if (replay.signature !== signature) throw new Error('LOYALTY_IDEMPOTENCY_CONFLICT');
      return clone(replay.result as T);
    }
    const result = action();
    idempotency.set(key, { signature, result: clone(result) });
    return clone(result);
  };

  const append = (input: Omit<LoyaltyEvent, 'eventId'>): LoyaltyEvent => {
    const event: LoyaltyEvent = { eventId: `loyalty:${++eventSequence}`, ...input };
    ledgerEvents.push(event);
    return clone(event);
  };

  const appendAccrualPair = (
    customerId: string,
    orderId: string,
    orderRevision: number,
    quantity: number,
    idempotencyKey: string,
    at: string,
  ): void => {
    if (quantity <= 0) return;
    append({
      type: 'growth_accrued', customerId, asset: 'growth', quantity, sourceType: 'sale',
      referenceId: orderId, idempotencyKey: `${idempotencyKey}:growth`, at, orderId, orderRevision,
    });
    append({
      type: 'seed_accrued', customerId, asset: 'memory_seed', quantity, sourceType: 'sale',
      referenceId: orderId, idempotencyKey: `${idempotencyKey}:memory_seed`, at, orderId, orderRevision,
    });
  };

  const appendReversalPair = (
    record: AccrualRecord,
    refundEvidenceId: string,
    quantity: number,
    idempotencyKey: string,
    at: string,
  ): void => {
    if (quantity <= 0) return;
    append({
      type: 'growth_reversed', customerId: record.customerId, asset: 'growth', quantity: -quantity,
      sourceType: 'refund', referenceId: refundEvidenceId, idempotencyKey: `${idempotencyKey}:growth`, at,
      orderId: record.orderId, orderRevision: record.orderRevision,
    });
    append({
      type: 'seed_reversed', customerId: record.customerId, asset: 'memory_seed', quantity: -quantity,
      sourceType: 'refund', referenceId: refundEvidenceId, idempotencyKey: `${idempotencyKey}:memory_seed`, at,
      orderId: record.orderId, orderRevision: record.orderRevision,
    });
  };

  return {
    accrueSale(command: AccrueSaleCommand): AccrueSaleResult {
      return run('accrueSale', command.idempotencyKey, command, () => {
        const customerId = assertText(command.customerId, 'LOYALTY_CUSTOMER_ID_REQUIRED');
        const acceptedOrderId = assertText(command.orderId, 'LOYALTY_ORDER_ID_REQUIRED');
        assertOrderRevision(command.orderRevision);
        assertTime(command.at);
        if (command.currency !== 'HKD') throw new Error('LOYALTY_CURRENCY_UNSUPPORTED');
        if (!Number.isSafeInteger(command.eligibleMinor) || command.eligibleMinor < 0) {
          throw new Error('LOYALTY_ELIGIBLE_AMOUNT_INVALID');
        }
        const key = orderKey(acceptedOrderId);
        if (accruals.has(key)) throw new Error('LOYALTY_ORDER_ALREADY_ACCRUED');
        const units = Math.floor(command.eligibleMinor / 100);
        const record: AccrualRecord = {
          customerId, orderId: acceptedOrderId, orderRevision: command.orderRevision,
          eligibleMinor: command.eligibleMinor, accruedUnits: units, cumulativeRefundMinor: 0, reversedUnits: 0,
        };
        accruals.set(key, record);
        appendAccrualPair(customerId, acceptedOrderId, command.orderRevision, units, command.idempotencyKey, command.at);
        return {
          customerId, orderId: acceptedOrderId, orderRevision: command.orderRevision,
          eligibleMinor: command.eligibleMinor, growthQuantity: units, memorySeedQuantity: units,
          balance: balanceFor(customerId),
        };
      });
    },

    reverseRefund(command: ReverseRefundCommand): ReverseRefundResult {
      return run('reverseRefund', command.idempotencyKey, command, () => {
        const customerId = assertText(command.customerId, 'LOYALTY_CUSTOMER_ID_REQUIRED');
        const acceptedOrderId = assertText(command.orderId, 'LOYALTY_ORDER_ID_REQUIRED');
        const evidenceId = assertText(command.refundEvidenceId, 'LOYALTY_REFUND_EVIDENCE_REQUIRED');
        assertOrderRevision(command.orderRevision);
        assertPositiveMinor(command.refundMinor, 'LOYALTY_REFUND_AMOUNT_INVALID');
        assertTime(command.at);
        if (appliedRefundEvidence.has(evidenceId)) throw new Error('LOYALTY_REFUND_EVIDENCE_ALREADY_APPLIED');
        const record = accruals.get(orderKey(acceptedOrderId));
        if (!record) throw new Error('LOYALTY_ORDER_NOT_ACCRUED');
        if (record.customerId !== customerId) throw new Error('LOYALTY_ACCRUAL_CUSTOMER_MISMATCH');
        if (record.orderRevision !== command.orderRevision) throw new Error('LOYALTY_ORDER_REVISION_MISMATCH');
        const nextRefundMinor = record.cumulativeRefundMinor + command.refundMinor;
        if (!Number.isSafeInteger(nextRefundMinor)) throw new Error('LOYALTY_REFUND_AMOUNT_INVALID');
        const nextReversedUnits = Math.min(record.accruedUnits, Math.floor(nextRefundMinor / 100));
        const delta = nextReversedUnits - record.reversedUnits;
        if (delta < 0) throw new Error('LOYALTY_REVERSAL_INVALID');
        appendReversalPair(record, evidenceId, delta, command.idempotencyKey, command.at);
        record.cumulativeRefundMinor = nextRefundMinor;
        record.reversedUnits = nextReversedUnits;
        appliedRefundEvidence.add(evidenceId);
        return {
          customerId, orderId: acceptedOrderId, refundEvidenceId: evidenceId,
          reversedGrowthQuantity: delta, reversedMemorySeedQuantity: delta,
          totalReversedGrowthQuantity: record.reversedUnits,
          totalReversedMemorySeedQuantity: record.reversedUnits,
          balance: balanceFor(customerId),
        };
      });
    },

    manualAdjust(command: ManualAdjustmentCommand): ManualAdjustmentResult {
      return run('manualAdjust', command.idempotencyKey, command, () => {
        const customerId = assertText(command.customerId, 'LOYALTY_CUSTOMER_ID_REQUIRED');
        const actorId = assertText(command.actorId, 'LOYALTY_ACTOR_REQUIRED');
        const reason = assertText(command.reason, 'LOYALTY_REASON_REQUIRED');
        assertTime(command.at);
        if (command.asset !== 'growth' && command.asset !== 'memory_seed') throw new Error('LOYALTY_ASSET_INVALID');
        if (!Number.isSafeInteger(command.quantity) || command.quantity === 0) {
          throw new Error('LOYALTY_ADJUSTMENT_QUANTITY_INVALID');
        }
        const before = balanceFor(customerId);
        const current = command.asset === 'growth' ? before.growth : before.memorySeed;
        if (current + command.quantity < 0) throw new Error('LOYALTY_BALANCE_NEGATIVE');
        const event = append({
          type: 'manual_adjustment', customerId, asset: command.asset, quantity: command.quantity,
          sourceType: 'manual', referenceId: command.idempotencyKey, idempotencyKey: command.idempotencyKey,
          at: command.at, actorId, reason,
        });
        return { event, balance: balanceFor(customerId) };
      });
    },

    transferCustomerAssets(command: TransferCustomerAssetsCommand): TransferCustomerAssetsResult {
      return run('transferCustomerAssets', command.idempotencyKey, command, () => {
        const sourceCustomerId = assertText(command.sourceCustomerId, 'LOYALTY_SOURCE_CUSTOMER_REQUIRED');
        const targetCustomerId = assertText(command.targetCustomerId, 'LOYALTY_TARGET_CUSTOMER_REQUIRED');
        const actorId = assertText(command.actorId, 'LOYALTY_ACTOR_REQUIRED');
        const reason = assertText(command.reason, 'LOYALTY_REASON_REQUIRED');
        assertTime(command.at);
        if (sourceCustomerId === targetCustomerId) throw new Error('LOYALTY_TRANSFER_SELF');
        const source = balanceFor(sourceCustomerId);
        const transferredEvents: LoyaltyEvent[] = [];
        const transferAsset = (asset: LoyaltyAsset, quantity: number): void => {
          if (quantity <= 0) return;
          transferredEvents.push(append({
            type: 'asset_transfer', customerId: sourceCustomerId, asset, quantity: -quantity,
            sourceType: 'customer_merge', referenceId: targetCustomerId,
            idempotencyKey: `${command.idempotencyKey}:${asset}:out`, at: command.at, actorId, reason,
          }));
          transferredEvents.push(append({
            type: 'asset_transfer', customerId: targetCustomerId, asset, quantity,
            sourceType: 'customer_merge', referenceId: sourceCustomerId,
            idempotencyKey: `${command.idempotencyKey}:${asset}:in`, at: command.at, actorId, reason,
          }));
        };
        transferAsset('growth', source.growth);
        transferAsset('memory_seed', source.memorySeed);
        return {
          sourceBalance: balanceFor(sourceCustomerId),
          targetBalance: balanceFor(targetCustomerId),
          events: transferredEvents,
        };
      });
    },

    balance(customerId: string): LoyaltyBalance {
      return balanceFor(customerId);
    },

    events(customerId: string): readonly LoyaltyEvent[] {
      const accepted = assertText(customerId, 'LOYALTY_CUSTOMER_ID_REQUIRED');
      return ledgerEvents.filter((event) => event.customerId === accepted).map(clone);
    },

    allEvents(): readonly LoyaltyEvent[] {
      return ledgerEvents.map(clone);
    },
  };
}
